import { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartInsights } from '@/components/ChartInsights';
import { DataField, Dataset } from '@/lib/types';
import {
  ChartBar,
  ChartLine,
  ChartPie,
  ChartScatter,
  Database,
  Download,
  Lightbulb,
  Sliders,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface DataVisualizationProps {
  datasets: Dataset[];
}

type ChartType = 'bar' | 'line' | 'scatter' | 'pie' | 'histogram';

interface ChartConfig {
  type: ChartType;
  xField?: string;
  yField?: string;
  categoryField?: string;
  dataset: string;
}

// Color palette for charts
const CHART_COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1',
  '#d084d0',
  '#ffb347',
  '#87d068',
  '#ffa39e',
  '#b7eb8f',
];

export function DataVisualization({ datasets }: DataVisualizationProps) {
  const [chartConfig, setChartConfig] = useState<ChartConfig>({
    type: 'bar',
    dataset: datasets[0]?.id || '',
  });

  const selectedDataset = datasets.find(d => d.id === chartConfig.dataset);

  // Get numeric and categorical fields for visualization options
  const numericFields = useMemo(
    () =>
      selectedDataset?.schema.filter(field => field.type === 'number') || [],
    [selectedDataset]
  );

  const categoricalFields = useMemo(
    () =>
      selectedDataset?.schema.filter(
        field => field.type === 'string' || field.type === 'boolean'
      ) || [],
    [selectedDataset]
  );

  const allFields = useMemo(
    () => selectedDataset?.schema || [],
    [selectedDataset]
  );

  // Generate chart data based on configuration
  const chartData = useMemo(() => {
    if (!selectedDataset || !selectedDataset.sampleData) {
      return [];
    }

    const data = selectedDataset.sampleData;

    if (chartConfig.type === 'pie') {
      if (!chartConfig.categoryField) {
        return [];
      }

      // Group by category and count
      const groups = data.reduce(
        (acc, row) => {
          const key = row[chartConfig.categoryField!]?.toString() || 'Unknown';

          acc[key] = (acc[key] || 0) + 1;

          return acc;
        },
        {} as Record<string, number>
      );

      return Object.entries(groups).map(([name, value]) => ({ name, value }));
    }

    if (chartConfig.type === 'histogram') {
      if (!chartConfig.xField) {
        return [];
      }

      // Create histogram bins
      const values = data
        .map(row => Number(row[chartConfig.xField!]))
        .filter(val => !isNaN(val))
        .sort((a, b) => a - b);

      if (values.length === 0) {
        return [];
      }

      const min = values[0];
      const max = values[values.length - 1];
      const binCount = Math.min(20, Math.ceil(Math.sqrt(values.length)));
      const binSize = (max - min) / binCount;

      const bins = Array.from({ length: binCount }, (_, i) => ({
        bin: `${(min + i * binSize).toFixed(1)}-${(min + (i + 1) * binSize).toFixed(1)}`,
        count: 0,
        binStart: min + i * binSize,
        binEnd: min + (i + 1) * binSize,
      }));

      values.forEach(value => {
        const binIndex = Math.min(
          Math.floor((value - min) / binSize),
          binCount - 1
        );

        bins[binIndex].count++;
      });

      return bins;
    }

    // For other chart types, return raw data with selected fields
    return data.map((row, index) => ({
      id: index,
      ...row,
      // Ensure numeric fields are properly converted
      ...(chartConfig.xField &&
      numericFields.find(f => f.name === chartConfig.xField)
        ? {
            [chartConfig.xField]: Number(row[chartConfig.xField]) || 0,
          }
        : {}),
      ...(chartConfig.yField &&
      numericFields.find(f => f.name === chartConfig.yField)
        ? {
            [chartConfig.yField]: Number(row[chartConfig.yField]) || 0,
          }
        : {}),
    }));
  }, [selectedDataset, chartConfig, numericFields]);

  const handleExportChart = () => {
    if (!chartData.length) {
      toast.error('No data to export');

      return;
    }

    // Convert chart data to CSV format
    const headers = Object.keys(chartData[0]);
    const csvContent = [
      headers.join(','),
      ...chartData.map(row =>
        headers
          .map(header =>
            typeof row[header] === 'string' && row[header].includes(',')
              ? `"${row[header]}"`
              : row[header]
          )
          .join(',')
      ),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    link.href = URL.createObjectURL(blob);
    link.download = `${selectedDataset?.name || 'chart'}_${chartConfig.type}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Chart data exported as CSV');
  };

  const renderChart = () => {
    if (!chartData.length || !selectedDataset) {
      return (
        <div className='flex items-center justify-center h-64 text-muted-foreground'>
          <div className='text-center'>
            <ChartBar className='w-12 h-12 mx-auto mb-2 opacity-50' />
            <p>Select fields to generate visualization</p>
          </div>
        </div>
      );
    }

    const commonProps = {
      width: '100%',
      height: 400,
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    switch (chartConfig.type) {
      case 'bar':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey={chartConfig.xField}
                tick={{ fontSize: 12 }}
                interval='preserveStartEnd'
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey={chartConfig.yField || 'value'}
                fill={CHART_COLORS[0]}
                name={chartConfig.yField || 'Value'}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer {...commonProps}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey={chartConfig.xField} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line
                type='monotone'
                dataKey={chartConfig.yField || 'value'}
                stroke={CHART_COLORS[1]}
                name={chartConfig.yField || 'Value'}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer {...commonProps}>
            <ScatterChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey={chartConfig.xField}
                type='number'
                tick={{ fontSize: 12 }}
              />
              <YAxis
                dataKey={chartConfig.yField || 'value'}
                type='number'
                tick={{ fontSize: 12 }}
              />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Scatter data={chartData} fill={CHART_COLORS[2]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer {...commonProps}>
            <PieChart>
              <Pie
                data={chartData}
                cx='50%'
                cy='50%'
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={120}
                fill='#8884d8'
                dataKey='value'
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'histogram':
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='bin'
                tick={{ fontSize: 12 }}
                interval={0}
                angle={-45}
                textAnchor='end'
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey='count' fill={CHART_COLORS[3]} name='Frequency' />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (datasets.length === 0) {
    return (
      <Card>
        <CardContent className='flex items-center justify-center h-64'>
          <div className='text-center text-muted-foreground'>
            <ChartBar className='w-16 h-16 mx-auto mb-4 opacity-50' />
            <h3 className='text-xl font-semibold mb-2'>No Datasets Selected</h3>
            <p className='mb-4'>
              Add datasets to start creating visualizations
            </p>
            <div className='flex gap-2'>
              <Button
                onClick={() => (window.location.hash = '#templates')}
                variant='default'
                className='gap-2'
              >
                <Lightbulb className='w-4 h-4' />
                Browse Templates
              </Button>
              <Button
                onClick={() => (window.location.hash = '#discover')}
                variant='outline'
                className='gap-2'
              >
                <Database className='w-4 h-4' />
                Find Datasets
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Chart Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Sliders className='w-5 h-5' />
            Visualization Settings
          </CardTitle>
          <CardDescription>
            Configure your chart type and data fields
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
            {/* Dataset Selection */}
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Dataset</label>
              <Select
                value={chartConfig.dataset}
                onValueChange={value =>
                  setChartConfig(prev => ({
                    ...prev,
                    dataset: value,
                    xField: undefined,
                    yField: undefined,
                    categoryField: undefined,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select dataset' />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map(dataset => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Chart Type Selection */}
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Chart Type</label>
              <Tabs
                value={chartConfig.type}
                onValueChange={value =>
                  setChartConfig(prev => ({
                    ...prev,
                    type: value as ChartType,
                    xField: undefined,
                    yField: undefined,
                    categoryField: undefined,
                  }))
                }
              >
                <TabsList className='grid grid-cols-5'>
                  <TabsTrigger value='bar' className='p-1'>
                    <ChartBar className='w-4 h-4' />
                  </TabsTrigger>
                  <TabsTrigger value='line' className='p-1'>
                    <ChartLine className='w-4 h-4' />
                  </TabsTrigger>
                  <TabsTrigger value='scatter' className='p-1'>
                    <ChartScatter className='w-4 h-4' />
                  </TabsTrigger>
                  <TabsTrigger value='pie' className='p-1'>
                    <ChartPie className='w-4 h-4' />
                  </TabsTrigger>
                  <TabsTrigger value='histogram' className='p-1'>
                    <ChartBar className='w-4 h-4' />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* X-Axis Field */}
            {chartConfig.type !== 'pie' && (
              <div className='space-y-2'>
                <label className='text-sm font-medium'>
                  {chartConfig.type === 'histogram' ? 'Field' : 'X-Axis'}
                </label>
                <Select
                  value={chartConfig.xField || ''}
                  onValueChange={value =>
                    setChartConfig(prev => ({ ...prev, xField: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select field' />
                  </SelectTrigger>
                  <SelectContent>
                    {(chartConfig.type === 'scatter'
                      ? numericFields
                      : allFields
                    ).map(field => (
                      <SelectItem key={field.name} value={field.name}>
                        <div className='flex items-center gap-2'>
                          <Badge variant='outline' className='text-xs'>
                            {field.type}
                          </Badge>
                          {field.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Y-Axis Field */}
            {chartConfig.type !== 'pie' && chartConfig.type !== 'histogram' && (
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Y-Axis</label>
                <Select
                  value={chartConfig.yField || ''}
                  onValueChange={value =>
                    setChartConfig(prev => ({ ...prev, yField: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select field' />
                  </SelectTrigger>
                  <SelectContent>
                    {numericFields.map(field => (
                      <SelectItem key={field.name} value={field.name}>
                        <div className='flex items-center gap-2'>
                          <Badge variant='outline' className='text-xs'>
                            {field.type}
                          </Badge>
                          {field.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Category Field for Pie Chart */}
            {chartConfig.type === 'pie' && (
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Category</label>
                <Select
                  value={chartConfig.categoryField || ''}
                  onValueChange={value =>
                    setChartConfig(prev => ({ ...prev, categoryField: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select category' />
                  </SelectTrigger>
                  <SelectContent>
                    {categoricalFields.map(field => (
                      <SelectItem key={field.name} value={field.name}>
                        <div className='flex items-center gap-2'>
                          <Badge variant='outline' className='text-xs'>
                            {field.type}
                          </Badge>
                          {field.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Export Button */}
            <div className='space-y-2'>
              <label className='text-sm font-medium opacity-0'>Export</label>
              <Button
                variant='outline'
                onClick={handleExportChart}
                className='w-full gap-2'
              >
                <Download className='w-4 h-4' />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart Display */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                {chartConfig.type === 'bar' && <ChartBar className='w-5 h-5' />}
                {chartConfig.type === 'line' && (
                  <ChartLine className='w-5 h-5' />
                )}
                {chartConfig.type === 'scatter' && (
                  <ChartScatter className='w-5 h-5' />
                )}
                {chartConfig.type === 'pie' && <ChartPie className='w-5 h-5' />}
                {chartConfig.type === 'histogram' && (
                  <ChartBar className='w-5 h-5' />
                )}
                {chartConfig.type.charAt(0).toUpperCase() +
                  chartConfig.type.slice(1)}{' '}
                Chart
              </CardTitle>
              <CardDescription>
                {selectedDataset?.name} - {chartData.length} data points
              </CardDescription>
            </div>
            <div className='flex gap-2'>
              <Badge variant='outline'>{chartConfig.type}</Badge>
              {chartConfig.xField && (
                <Badge variant='secondary'>X: {chartConfig.xField}</Badge>
              )}
              {chartConfig.yField && (
                <Badge variant='secondary'>Y: {chartConfig.yField}</Badge>
              )}
              {chartConfig.categoryField && (
                <Badge variant='secondary'>
                  Category: {chartConfig.categoryField}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>{renderChart()}</CardContent>
      </Card>

      {/* Automatic Insights */}
      <ChartInsights
        datasets={datasets}
        chartData={chartData}
        chartConfig={chartConfig}
      />

      {/* Chart Statistics */}
      {chartData.length > 0 && selectedDataset && (
        <Card>
          <CardHeader>
            <CardTitle>Quick Statistics</CardTitle>
            <CardDescription>
              Basic numerical summary of your data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='p-4 bg-muted/50 rounded-lg'>
                <div className='text-sm text-muted-foreground'>Data Points</div>
                <div className='text-2xl font-bold'>{chartData.length}</div>
              </div>

              {chartConfig.type !== 'pie' &&
                chartConfig.yField &&
                numericFields.find(f => f.name === chartConfig.yField) && (
                  <>
                    <div className='p-4 bg-muted/50 rounded-lg'>
                      <div className='text-sm text-muted-foreground'>
                        Max Value
                      </div>
                      <div className='text-2xl font-bold'>
                        {Math.max(
                          ...chartData.map(
                            d => Number(d[chartConfig.yField!]) || 0
                          )
                        ).toFixed(2)}
                      </div>
                    </div>

                    <div className='p-4 bg-muted/50 rounded-lg'>
                      <div className='text-sm text-muted-foreground'>
                        Average
                      </div>
                      <div className='text-2xl font-bold'>
                        {(
                          chartData.reduce(
                            (sum, d) =>
                              sum + (Number(d[chartConfig.yField!]) || 0),
                            0
                          ) / chartData.length
                        ).toFixed(2)}
                      </div>
                    </div>
                  </>
                )}

              {chartConfig.type === 'pie' && (
                <>
                  <div className='p-4 bg-muted/50 rounded-lg'>
                    <div className='text-sm text-muted-foreground'>
                      Categories
                    </div>
                    <div className='text-2xl font-bold'>{chartData.length}</div>
                  </div>

                  <div className='p-4 bg-muted/50 rounded-lg'>
                    <div className='text-sm text-muted-foreground'>
                      Largest Segment
                    </div>
                    <div className='text-2xl font-bold'>
                      {Math.max(...chartData.map(d => d.value)).toFixed(0)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
